import type {
  ControllerFieldState,
  ControllerRenderProps,
} from 'react-hook-form';
import { forwardRef } from 'react';

import { usePanelContentsSubscribe } from '../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel.hook';

import { TextField } from '../FinanceTextField/FinanceTextField.styled';

import { CURRENCIES } from '../../../../../types';
import { type TextFieldProps } from '../../../../Toolkit/Inputs/TextField';
import { preventSubmit } from '../preventSubmit';

type PriceFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value?: number;
} & Omit<ControllerRenderProps, 'value'> &
  ControllerFieldState;

const PRICE_PLACEHOLDER = '€0';

const MAX_LENGTH_OF_NINE_DIGITS_WITH_THOUSANDS_SEPARATED_BY_COMMAS_WITH_CURRENCY_SYMBOL = 12;

const formatPrice = (value?: number) => {
  return typeof value === 'number'
    ? new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: CURRENCIES.EUR,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(value)
    : '';
};

const handleChange =
  (onChange: (value: number | undefined) => void) => (e: string) => {
    const rawValue = e.replace(/[^\d]/g, '');
    onChange(rawValue ? Number(rawValue) : undefined);
  };

export const PriceField = forwardRef<HTMLInputElement, PriceFieldProps>(
  (props, ref) => {
    const { value, onChange, invalid, error, ...rest } = props;
    return (
      <TextField
        {...rest}
        placeholder={PRICE_PLACEHOLDER}
        // Determine placeholder value based on unformatted value
        isPlaceholder={value === 0}
        value={formatPrice(value)}
        onChange={handleChange(onChange)}
        hasError={invalid}
        errorMessage={error?.message}
        ref={ref}
        inputMode="numeric"
        maxLength={
          MAX_LENGTH_OF_NINE_DIGITS_WITH_THOUSANDS_SEPARATED_BY_COMMAS_WITH_CURRENCY_SYMBOL
        }
        onKeyPress={preventSubmit}
      />
    );
  },
);

PriceField.displayName = 'PriceField';

type FinancePriceFieldProps = Omit<
  TextFieldProps,
  'hasError' | 'errorMessage' | 'name' | 'value' | 'onChange'
> & {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
} & ControllerFieldState & {
    name: string;
    isOptional?: boolean;
  };

export const FinancePriceField = forwardRef<
  HTMLInputElement,
  FinancePriceFieldProps
>((props, ref) => {
  const {
    value,
    onChange,
    invalid,
    isOptional = false,
    error,
    ...rest
  } = props;
  usePanelContentsSubscribe({
    name: props.name,
    value,
    invalid,
    isOptional,
  });
  return (
    <TextField
      {...rest}
      placeholder={PRICE_PLACEHOLDER}
      value={formatPrice(value)}
      // Determine placeholder value based on unformatted value
      isPlaceholder={value === 0}
      hasError={invalid}
      errorMessage={error?.message}
      ref={ref}
      onChange={handleChange(onChange)}
      inputMode="numeric"
      maxLength={
        MAX_LENGTH_OF_NINE_DIGITS_WITH_THOUSANDS_SEPARATED_BY_COMMAS_WITH_CURRENCY_SYMBOL
      }
      onKeyPress={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      }}
    />
  );
});

FinancePriceField.displayName = 'FinancePriceField';
